import { usePolling } from "../lib/use-polling";
import { api } from "../lib/api";
import { Table, type Column } from "../components/primitives/Table";
import { Badge } from "../components/primitives/Badge";
import { Pulse } from "../components/primitives/Pulse";
import { InfoTip } from "../components/primitives/InfoTip";
import { PageHelp } from "../components/PageHelp";

const CONNECTOR_PREFIX = "jump-pipelines/connectors/";

/**
 * Read-only audit view of every secret under the jump-pipelines/ prefix.
 *
 * Connector credentials are created + rotated through the Connection editor
 * (the secret name is derived from the connector instance id; operators never
 * type or memorize it). Platform secrets are managed out-of-band (Terraform,
 * AWS console) and remain read-only here by design.
 *
 * This page exists for ops visibility — confirm secrets exist, see when they
 * were last rotated. Mutations happen on the resource that owns the secret.
 */
export function Secrets() {
  const q = usePolling(() => api.listSecrets(), 60000);

  if (q.status === "loading") return <Pulse />;

  const columns: Column<{ name: string }>[] = [
    { key: "name", label: "NAME", mono: true, render: (r) => r.name },
    {
      key: "scope",
      label: "SCOPE",
      render: (r) => (
        <Badge tone={r.name.startsWith(CONNECTOR_PREFIX) ? "ok" : "muted"}>
          {r.name.startsWith(CONNECTOR_PREFIX) ? "CONNECTOR" : "PLATFORM"}
        </Badge>
      ),
    },
    {
      key: "managed-by",
      label: "MANAGED BY",
      render: (r) => (
        <span className="uppercase-label">
          {r.name.startsWith(CONNECTOR_PREFIX) ? "Connection editor" : "Terraform / Ops"}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="uppercase-label" style={{ display: "inline-flex", alignItems: "center" }}>
          SECRETS
          <InfoTip termKey="secret" />
        </div>
      </div>
      <PageHelp />
      <Table columns={columns} rows={q.data?.secrets ?? []} rowKey={(r) => r.name} />
    </div>
  );
}
